import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Button, TextField, MenuItem, CircularProgress,
  Chip, IconButton, Tooltip, Paper, Alert,
} from "@mui/material";
import AddIcon    from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import LinkIcon   from "@mui/icons-material/Link";
import { useNavigate } from "react-router-dom";
import api from "../../../utils/api";

const LINK_TYPES = [
  { value:"related",   label:"Related" },
  { value:"duplicate", label:"Duplicate" },
  { value:"blocks",    label:"Blocks" },
  { value:"blocked_by",label:"Blocked By" },
];

const WOLinksTab = ({ workOrderId }) => {
  const [links,     setLinks]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showAdd,   setShowAdd]   = useState(false);
  const [linkedId,  setLinkedId]  = useState("");
  const [linkType,  setLinkType]  = useState("related");
  const [error,     setError]     = useState("");
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/work-orders/${workOrderId}/links`); setLinks(r.data||[]); }
    catch { console.error("Failed to load links"); }
    finally { setLoading(false); }
  }, [workOrderId]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!linkedId) { setError("Enter a Work Order ID to link."); return; }
    setError("");
    try {
      await api.post(`/work-orders/${workOrderId}/links`, { linked_order_id: parseInt(linkedId,10), link_type: linkType });
      setLinkedId(""); setShowAdd(false); load();
    } catch (err) { setError(err.response?.data?.message || "Failed to link."); }
  };

  const LINK_COLOR = { related:"#2f63ad", duplicate:"#dc2626", blocks:"#b45309", blocked_by:"#7c3aed" };

  return (
    <Box>
      <Box sx={{ display:"flex", justifyContent:"space-between", alignItems:"center", mb:2 }}>
        <Typography variant="h6" sx={{ fontWeight:700, color:"#223142" }}>
          Linked Work Orders {links.length > 0 && `(${links.length})`}
        </Typography>
        {!showAdd && <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={()=>setShowAdd(true)}
          sx={{ borderRadius:"10px", textTransform:"none", fontWeight:700, bgcolor:"#2f63ad", boxShadow:"none" }}>Link WO</Button>}
      </Box>

      {showAdd && (
        <Paper elevation={0} sx={{ p:2, mb:2, borderRadius:"12px", border:"1px solid rgba(47,99,173,0.12)", bgcolor:"rgba(47,99,173,0.02)" }}>
          {error && <Alert severity="error" sx={{ mb:1.5, borderRadius:2 }}>{error}</Alert>}
          <Box sx={{ display:"flex", gap:1.5, alignItems:"flex-start", flexWrap:"wrap" }}>
            <TextField size="small" label="Work Order ID" type="number" value={linkedId} onChange={e=>setLinkedId(e.target.value)}
              sx={{ width:140, "& .MuiOutlinedInput-root":{ borderRadius:"10px" } }} />
            <TextField select size="small" label="Relationship" value={linkType} onChange={e=>setLinkType(e.target.value)}
              sx={{ width:150, "& .MuiOutlinedInput-root":{ borderRadius:"10px" } }}>
              {LINK_TYPES.map(lt => <MenuItem key={lt.value} value={lt.value}>{lt.label}</MenuItem>)}
            </TextField>
            <Button variant="contained" onClick={handleAdd}
              sx={{ borderRadius:"10px", textTransform:"none", fontWeight:700, bgcolor:"#2f63ad", boxShadow:"none" }}>Add</Button>
            <Button variant="outlined" onClick={()=>{ setShowAdd(false); setError(""); setLinkedId(""); }}
              sx={{ borderRadius:"10px", textTransform:"none", fontWeight:600 }}>Cancel</Button>
          </Box>
        </Paper>
      )}

      {loading ? <CircularProgress size={24} /> : links.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius:"10px" }}>No linked work orders. Use "Link WO" to connect related requests.</Alert>
      ) : (
        <Box sx={{ display:"flex", flexDirection:"column", gap:1 }}>
          {links.map(l => (
            <Box key={l.id} sx={{ display:"flex", alignItems:"center", gap:1.5, p:1.5, borderRadius:"10px",
              border:"1px solid rgba(47,99,173,0.1)", bgcolor:"rgba(47,99,173,0.02)", cursor:"pointer" }}
              onClick={() => navigate(`/home/workorders/${l.linked_order_id}`)}>
              <LinkIcon sx={{ color:"#9ca3af", fontSize:18 }} />
              <Chip size="small" label={LINK_TYPES.find(t=>t.value===l.link_type)?.label||l.link_type}
                sx={{ fontWeight:700, fontSize:11, borderRadius:"8px",
                  bgcolor:`${LINK_COLOR[l.link_type]||"#2f63ad"}15`, color:LINK_COLOR[l.link_type]||"#2f63ad" }} />
              <Box sx={{ flex:1 }}>
                <Typography variant="body2" sx={{ fontWeight:700, color:"#2f63ad" }}>
                  WO #{l.linked_order_id}
                </Typography>
                <Typography variant="caption" sx={{ color:"#6b778c" }}>
                  {l.description || "No description"} · {l.status}
                </Typography>
              </Box>
              <Tooltip title="Remove link">
                <IconButton size="small" color="error" onClick={async e => { e.stopPropagation(); await api.delete(`/wo-links/${l.id}`); load(); }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default WOLinksTab;
